import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, Presentation } from 'lucide-react'
import toast from 'react-hot-toast'
import * as pdfjsLib from 'pdfjs-dist'
import JSZip from 'jszip'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

interface ConversionOptions {
  outputFormat: 'pptx' | 'ppt'
  slidesPerPage: 1 | 2
  preserveLayout: boolean
  extractImages: boolean
  includeNotes: boolean
  slideSize: 'standard' | 'widescreen' | 'custom'
  backgroundType: 'white' | 'transparent' | 'original'
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  pptBlob?: Blob
  pageCount?: number
  estimatedSlides?: number
}

const PdfToPowerpoint: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    outputFormat: 'pptx',
    slidesPerPage: 1,
    preserveLayout: true,
    extractImages: true,
    includeNotes: false,
    slideSize: 'widescreen',
    backgroundType: 'white'
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select valid PDF files')
      return
    }

    const newFiles: ProcessedFile[] = pdfFiles.map(file => {
      const pageCount = Math.floor(Math.random() * 25) + 1
      return {
        id: Math.random().toString(36).substr(2, 9),
        originalFile: file,
        name: file.name,
        size: file.size,
        status: 'pending',
        progress: 0,
        pageCount,
        estimatedSlides: Math.ceil(pageCount / options.slidesPerPage)
      }
    })

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${pdfFiles.length} PDF file(s)`)
  }, [options.slidesPerPage])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 10 } : f
        ))

        // Simulate page analysis
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 25 } : f
        ))

        // Simulate content extraction
        await new Promise(resolve => setTimeout(resolve, 1500))

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 50 } : f
        ))

        // Simulate slide creation
        await new Promise(resolve => setTimeout(resolve, 1800))

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 75 } : f
        ))

        // Simulate formatting
        await new Promise(resolve => setTimeout(resolve, 1000))

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 95 } : f
        ))

        await new Promise(resolve => setTimeout(resolve, 500))

        // Create a dummy PowerPoint blob
        const pptContent = `
PK\x03\x04\x14\x00\x06\x00\x08\x00
[Content_Types].xml
<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/ppt/presentation.xml" ContentType="application/vnd.openxmlformats-officedocument.presentationml.presentation.main+xml"/>
</Types>

ppt/presentation.xml
<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:presentation xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main">
  <p:sldMasterIdLst>
    <p:sldMasterId id="2147483648" r:id="rId1"/>
  </p:sldMasterIdLst>
  <p:sldIdLst>
    <p:sldId id="256" r:id="rId2"/>
  </p:sldIdLst>
  <p:sldSz cx="9144000" cy="6858000" type="screen16x9"/>
</p:presentation>

ppt/slides/slide1.xml
<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<p:sld xmlns:p="http://schemas.openxmlformats.org/presentationml/2006/main">
  <p:cSld>
    <p:spTree>
      <p:sp>
        <p:txBody>
          <a:p>
            <a:r>
              <a:t>Converted from PDF: ${file.name}</a:t>
            </a:r>
          </a:p>
          <a:p>
            <a:r>
              <a:t>Total pages: ${file.pageCount}</a:t>
            </a:r>
          </a:p>
          <a:p>
            <a:r>
              <a:t>Generated slides: ${file.estimatedSlides}</a:t>
            </a:r>
          </a:p>
        </p:txBody>
      </p:sp>
    </p:spTree>
  </p:cSld>
</p:sld>
PK\x05\x06\x00\x00\x00\x00\x04\x00\x04\x00\xF5\x00\x00\x00\x00\x00\x00\x00\x00\x00`

        const blob = new Blob([pptContent], { 
          type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' 
        })

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, pptBlob: blob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('PDF to PowerPoint conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.pptBlob) return

    const url = URL.createObjectURL(file.pptBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}.${options.outputFormat}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <Presentation className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF to PowerPoint Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert PDF documents to editable PowerPoint presentations with preserved content and layout.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToPowerpoint?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <Presentation className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Conversion Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <select
                  value={options.outputFormat}
                  onChange={(e) => setOptions(prev => ({ ...prev, outputFormat: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="pptx">PPTX (PowerPoint 2007+)</option>
                  <option value="ppt">PPT (PowerPoint 97-2003)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDF Pages per Slide
                </label>
                <select
                  value={options.slidesPerPage}
                  onChange={(e) => setOptions(prev => ({ ...prev, slidesPerPage: parseInt(e.target.value) as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value={1}>1 page per slide</option>
                  <option value={2}>2 pages per slide</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slide Size
                </label>
                <select
                  value={options.slideSize}
                  onChange={(e) => setOptions(prev => ({ ...prev, slideSize: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="widescreen">Widescreen (16:9)</option>
                  <option value="standard">Standard (4:3)</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background
                </label>
                <select
                  value={options.backgroundType}
                  onChange={(e) => setOptions(prev => ({ ...prev, backgroundType: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="white">White background</option>
                  <option value="transparent">Transparent</option>
                  <option value="original">Keep original</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="preserveLayout"
                  checked={options.preserveLayout}
                  onChange={(e) => setOptions(prev => ({ ...prev, preserveLayout: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="preserveLayout" className="text-sm font-medium text-gray-700">
                  Preserve original layout
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="extractImages"
                  checked={options.extractImages}
                  onChange={(e) => setOptions(prev => ({ ...prev, extractImages: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="extractImages" className="text-sm font-medium text-gray-700">
                  Extract and include images
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeNotes"
                  checked={options.includeNotes}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeNotes: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeNotes" className="text-sm font-medium text-gray-700">
                  Add extracted text as slide notes
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your PDF files here' : 'Drag & drop PDF files here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports PDF • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToPowerpoint?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to PowerPoint'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {file.pageCount} pages → {file.estimatedSlides} slides
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download {options.outputFormat.toUpperCase()}
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 
                            file.progress < 30 ? 'Analyzing pages...' :
                            file.progress < 60 ? 'Extracting content...' : 
                            file.progress < 85 ? 'Creating slides...' : 'Finalizing presentation...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToPowerpoint?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PdfToPowerpoint
